<template>
  <div>
    <v-dialog
      v-model="show"
      width="700px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
    >
      <v-card style="display:flex;flex-direction:column;max-height:90vh;">
        <v-card-title>
          <span>License Summary</span>
          <v-spacer />
          <v-btn
            v-if="$vuetify.breakpoint.smAndDown"
            icon
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="flex:1 1 auto;overflow-y:auto;">
          <!-- Summary stats -->
          <v-row dense class="mb-3">
            <v-col cols="4">
              <v-card outlined class="text-center pa-2">
                <div class="text-h5 font-weight-bold primary--text">
                  {{ summary.maxLicense || 0 }}
                </div>
                <div class="caption grey--text">Max</div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card
                outlined
                class="text-center pa-2"
                :color="summary.isOverLimit ? 'error lighten-5' : ''"
              >
                <div
                  class="text-h5 font-weight-bold"
                  :class="summary.isOverLimit ? 'error--text' : ''"
                >
                  {{ summary.registeredDevices || 0 }}
                </div>
                <div class="caption grey--text">Slots Used</div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card outlined class="text-center pa-2">
                <div
                  class="text-h5 font-weight-bold"
                  :class="(summary.remainingDevices || 0) <= 0 ? 'error--text' : 'success--text'"
                >
                  {{ summary.remainingDevices || 0 }}
                </div>
                <div class="caption grey--text">Remaining</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Superuser-only: edit license code and max -->
          <template v-if="isSuperUser">
            <v-divider class="mb-3" />
            <div class="subtitle-2 mb-2">License Settings</div>
            <div class="d-flex align-center">
              <v-text-field
                v-model="companyCode"
                label="License Code"
                hide-details
              />
              <v-btn
                icon
                class="ml-2"
                color="primary"
                :disabled="!trimmedCompanyCode"
                :title="trimmedCompanyCode ? 'Show license code as QR' : 'Enter a license code first'"
                @click="openLicenseQr"
              >
                <v-icon>mdi-qrcode</v-icon>
              </v-btn>
              <v-icon
                class="ml-3"
                color="success"
                @click="updateCode"
              >
                mdi-check
              </v-icon>
            </div>
            <div class="d-flex align-center mt-3">
              <v-text-field
                v-model="companyMaxLicense"
                type="number"
                label="Maximum License"
                hide-details
              />
              <v-icon
                class="ml-3"
                color="success"
                @click="updateMaxLicensee"
              >
                mdi-check
              </v-icon>
            </div>
          </template>

          <!-- Enrolled devices list -->
          <v-divider class="my-4" />

          <div class="d-flex align-center justify-space-between mb-2">
            <div class="subtitle-2">
              Enrolled Devices ({{ enrolledDevices.length }} unique
              <span
                v-if="summary.registeredDevices && summary.registeredDevices !== enrolledDevices.length"
                class="grey--text font-weight-regular"
              > / {{ summary.registeredDevices }} slots</span>)
            </div>
            <v-btn
              v-if="isSuperUser && selectedDeviceIds.length > 0"
              small
              color="error"
              :loading="unenrolling"
              @click="unenrollSelected"
            >
              <v-icon small class="mr-1">mdi-account-remove</v-icon>
              Unenroll ({{ selectedDeviceIds.length }})
            </v-btn>
          </div>

          <v-progress-linear
            v-if="loadingSummary || loadingDevices"
            indeterminate
            color="primary"
            class="mb-2"
          />

          <!-- Table header row -->
          <v-row
            dense
            class="caption grey--text text-uppercase font-weight-medium px-2 mb-1"
            no-gutters
            align="center"
          >
            <v-col v-if="isSuperUser" style="flex:0 0 32px;max-width:32px;" />
            <v-col style="flex:0 0 44px;max-width:44px;" class="text-center">Image</v-col>
            <v-col class="pl-2">Name</v-col>
            <v-col style="flex:0 0 140px;max-width:140px;">Device Id</v-col>
          </v-row>
          <v-divider class="mb-1" />

          <v-row
            v-for="device in enrolledDevices"
            :key="device.id"
            dense
            align="center"
            class="px-2 py-1"
            no-gutters
            style="border-bottom:1px solid rgba(0,0,0,0.05);"
          >
            <v-col v-if="isSuperUser" style="flex:0 0 32px;max-width:32px;">
              <v-checkbox
                v-model="selectedDeviceIds"
                :value="device.id"
                hide-details
                class="ma-0 pa-0"
                dense
              />
            </v-col>
            <v-col style="flex:0 0 44px;max-width:44px;">
              <v-img
                :src="deviceImageSrc(device.deviceModel)"
                height="36"
                width="36"
                contain
              />
            </v-col>
            <v-col class="body-2 pl-2 pr-1" style="word-break:break-word;min-width:0;">
              {{ device.name || device.deviceModel || '—' }}
            </v-col>
            <v-col
              class="caption grey--text text--darken-2"
              style="flex:0 0 140px;max-width:140px;word-break:break-all;"
            >
              {{ device.macAddress || device.id }}
            </v-col>
          </v-row>

          <div
            v-if="!loadingSummary && !loadingDevices && enrolledDevices.length === 0"
            class="text-center grey--text py-6"
          >
            No enrolled devices found.
          </div>
        </v-card-text>

        <v-card-actions style="flex:0 0 auto;">
          <v-spacer />
          <v-btn
            text
            @click="close"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- License code as a scannable QR -->
    <v-dialog
      v-model="licenseQrDialog"
      max-width="360"
    >
      <v-card>
        <v-card-title class="text-subtitle-1">
          License Code
          <v-spacer />
          <v-btn
            icon
            small
            @click="licenseQrDialog = false"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="text-center pb-2">
          <div class="license-qr">
            <qr-code
              :value="trimmedCompanyCode"
              :size="240"
            />
          </div>
          <div class="license-qr__code mt-3">{{ trimmedCompanyCode }}</div>
          <div
            v-if="company && company.name"
            class="caption grey--text"
          >
            {{ company.name }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn
            text
            small
            color="primary"
            @click="copyLicenseCode"
          >
            <v-icon left small>mdi-content-copy</v-icon> Copy code
          </v-btn>
          <v-spacer />
          <v-btn
            text
            small
            @click="licenseQrDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import QrCode from './QRCode.vue'

  export default {
    name: 'CompanyLicenseeDialog',
    components: {
      QrCode,
    },
    props: ['value', 'company'],
    data () {
      return {
        companyCode: '',
        companyMaxLicense: 0,
        summary: {},
        allCompanyDevices: [],
        selectedDeviceIds: [],
        loadingSummary: false,
        loadingDevices: false,
        unenrolling: false,
        licenseQrDialog: false,
      }
    },
    computed: {
      trimmedCompanyCode () {
        return String(this.companyCode || '').trim()
      },

      show: {
        get () { return this.value },
        set (val) { this.$emit('input', val) },
      },
      isSuperUser () {
        return this.$store.getters['authUser/isSuperUser']
      },
      enrolledDevices () {
        if (!this.summary.deviceIds?.length) return []
        const uniqueIds = [...new Set(this.summary.deviceIds)]
        return uniqueIds.map(id => {
          const found = this.allCompanyDevices.find(
            d => d.id === id || d.deviceId === id,
          )
          return found || { id, deviceModel: '', macAddress: '', name: '' }
        })
      },
    },
    watch: {
      value (val) {
        if (val && this.company) {
          this.companyCode = this.company.code || ''
          this.companyMaxLicense = this.company.maxLicense || 0
          this.loadSummary()
        }
      },
      company: {
        handler (val) {
          if (val) {
            this.companyCode = val.code || ''
            this.companyMaxLicense = val.maxLicense || 0
          }
        },
        deep: true,
      },
    },
    methods: {
      openLicenseQr () {
        if (!this.trimmedCompanyCode) {
          return
        }
        this.licenseQrDialog = true
      },

      async copyLicenseCode () {
        try {
          await navigator.clipboard.writeText(this.trimmedCompanyCode)
          this.$toast.success('License code copied.', 'Success', { position: 'topRight' })
        } catch (error) {
          // Clipboard access needs a secure context; the code is on screen anyway.
          this.$toast.error('Could not copy. Select the code and copy it manually.', 'Error', {
            position: 'topRight',
          })
        }
      },

      async loadSummary () {
        if (!this.company?.id) return
        this.loadingSummary = true
        this.summary = {}
        this.allCompanyDevices = []
        this.selectedDeviceIds = []
        try {
          const res = await this.$http.get(`Company/License/${this.company.id}/Summary`)
          if (res.data?.success) {
            this.summary = res.data.data || {}
            if (this.summary.deviceIds?.length) {
              this.loadDeviceDetails()
            }
          }
        } catch (e) {
          // ignore network errors
        } finally {
          this.loadingSummary = false
        }
      },
      async loadDeviceDetails () {
        this.loadingDevices = true
        try {
          const res = await this.$http.get('Device/GetAllDevices', {
            params: { companyId: this.company.id, page: 1, limit: 10000 },
          })
          // API returns { total, data: [...] }
          const items = res.data?.data
          this.allCompanyDevices = Array.isArray(items) ? items : []
        } catch (e) {
          // ignore
        } finally {
          this.loadingDevices = false
        }
      },
      async unenrollSelected () {
        if (!this.selectedDeviceIds.length) return
        this.unenrolling = true
        try {
          const res = await this.$http.post(
            `Company/License/${this.company.id}/UnenrollDevices`,
            { deviceIds: this.selectedDeviceIds },
          )
          if (res.data?.success) {
            this.$toast.success('Devices unenrolled successfully.', 'Success', { position: 'topRight' })
            this.selectedDeviceIds = []
            await this.loadSummary()
          } else {
            this.$toast.error(res.data?.message || 'Unenroll failed.', 'Error', { position: 'topRight' })
          }
        } catch (e) {
          this.$toast.error('Unenroll failed.', 'Error', { position: 'topRight' })
        } finally {
          this.unenrolling = false
        }
      },
      deviceImageSrc (model) {
        if (!model) return require('@/assets/defaultDevice.png')
        if (model === 'TC21') return require('@/assets/TC21&26.jpg')
        if (model.includes('EDA52')) return require('@/assets/EDA52.png')
        if (model.includes('TC57X')) return require('@/assets/TC57X.jpg')
        if (model.includes('MC33')) return require('@/assets/MC33.png')
        if (model.includes('WT63')) return require('@/assets/WT63B0.jpg')
        if (model.includes('CK65')) return require('@/assets/ck65.png')
        return require('@/assets/defaultDevice.png')
      },
      updateCode () {
        const data = new FormData()
        data.append('Id', this.company.id)
        data.append('Code', this.companyCode)
        this.$http.put('Company/UpdateCode', data).then((response) => {
          if (response.data.success) {
            this.$emit('company', response.data.data)
            this.$toast.success(response.data.message, 'Success', { position: 'topRight' })
          } else {
            this.$toast.error(response.data.message, 'Error', { position: 'topRight' })
          }
        })
      },
      updateMaxLicensee () {
        const data = new FormData()
        data.append('Id', this.company.id)
        data.append('MaxLicense', this.companyMaxLicense)
        this.$http.put('Company/UpdateMaxLicense', data).then((response) => {
          if (response.data.success) {
            this.$emit('company', response.data.data)
            this.$toast.success(response.data.message, 'Success', { position: 'topRight' })
          } else {
            this.$toast.error(response.data.message, 'Error', { position: 'topRight' })
          }
        })
      },
      close () {
        this.show = false
      },
    },
  }
</script>

<style scoped>
.license-qr {
  display: inline-block;
  padding: 12px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.license-qr__code {
  font-family: monospace;
  font-size: 1.1rem;
  letter-spacing: 1px;
  word-break: break-all;
}
</style>
